import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalShipping as PickingIcon,
} from "@mui/icons-material";
import {
  useTSQWorkOrderListView,
  useTSMDeactivateWO,
  useTSMReactivateWO,
  useTSMCompleteWO,
  useTSMVoidWO,
} from "../../hooks/useTSWorkOrder";
import { ListRequestFilter } from "../../types/common/commonTypes";
import { WorkOrderListView } from "../../types/workOrderTypes";
import { paginationDefaults } from "../../config/settingsDefaults";
import stringUtils from "../../utils/stringUtils";
import statusUtils from "../../utils/statusUtils";
import { useAuth } from "../../contexts/AuthContextWithQuery";

const FilterBox: React.FC<{
  finalSet: React.Dispatch<React.SetStateAction<string>>;
}> = ({ finalSet }) => {
  const [localVal, setLocalVal] = useState<string>("");
  return (
    <TextField
      label="Search"
      value={localVal}
      onChange={(e) => setLocalVal(e.target.value)}
      onBlur={(e) => finalSet(e.target.value)}
      placeholder="Search WOs by number, status or Item number..."
      slotProps={{
        input: {
          startAdornment: (
            <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
          ),
        },
      }}
      sx={{ minWidth: 300 }}
    />
  );
};

export const PageWorkOrderList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [searchTerm, setSearchTerm] = useState("");

  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });

  const [error, setError] = useState<string | null>(null);

  const [orderBy, setOrderBy] = useState<string>("work_orders.wo_date");
  const [order, setOrder] = useState<"asc" | "desc">("desc");

  const [recTotal, setRecTotal] = useState<number>(0);
  const [foundWOs, setFoundWOs] = useState<WorkOrderListView[]>([]);
  const [shouldSearch, setShouldSearch] = useState<boolean>(true);
  const refShouldSearch = useRef(shouldSearch);

  useEffect(() => {
    refShouldSearch.current = shouldSearch;
  });

  const {
    data: woResponse,
    isLoading,
    error: queryError,
    isPlaceholderData,
  } = useTSQWorkOrderListView(
    {
      page: pagination.page,
      page_size: pagination.size,
      sort_by: `${orderBy} ${order.toLocaleUpperCase()}`,
      filter:
        searchTerm === "" || !shouldSearch
          ? null
          : `work_orders.number ILIKE '%${searchTerm}%' OR work_orders.order_status::text ILIKE '%${searchTerm}%' OR work_orders.build_status::text ILIKE '%${searchTerm}%' OR items.number ILIKE '%${searchTerm}%'`,
    },
    "work_order_list",
    shouldSearch,
  );

  useEffect(() => {
    if (refShouldSearch.current) {
      if (woResponse?.items) {
        setFoundWOs(woResponse.items);
        setRecTotal(woResponse.total);
        setShouldSearch(false);
      }
    }
  }, [woResponse]);

  const deactivateWOMutation = useTSMDeactivateWO();
  const reactivateWOMutation = useTSMReactivateWO();
  const completeWOMutation = useTSMCompleteWO();
  const voidWOMutation = useTSMVoidWO();

  const handleDeactivate = async (id: string) => {
    if (!window.confirm("Are you sure you want to deactivate this WO?")) {
      return;
    }

    try {
      await deactivateWOMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to deactivate WO:", err);
      setError(err.response?.data?.detail || "Failed to deactivate WO");
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await reactivateWOMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to reactivate WO:", err);
      setError(err.response?.data?.detail || "Failed to reactivate WO");
    }
  };

  const completeOrder = async (id: string) => {
    if (!id) {
      setError("Missing ID, Cannot Complete Order");
      return;
    }
    try {
      await completeWOMutation.mutateAsync(id);
    } catch (e: any) {
      setError("There was an error marking WO Complete");
      console.error(e);
      return;
    }
  };

  const voidOrder = async (id: string) => {
    if (!id) {
      setError("Missing ID, Cannot VOID Order");
      return;
    }
    try {
      await voidWOMutation.mutateAsync(id);
    } catch (e: any) {
      setError("There was an error marking WO VOID");
      console.error(e);
      return;
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    setShouldSearch(true);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
    setShouldSearch(true);
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setShouldSearch(true);
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Work Orders
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/manufacturing/orders/form?mode=create")}
        >
          Create New WO
        </Button>
      </Stack>

      {(error || queryError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || queryError?.message || "An unknown error occurred"}
        </Alert>
      )}

      {shouldSearch && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading updated data...
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FilterBox finalSet={setSearchTerm} />
          <Button
            variant="contained"
            color="info"
            onClick={() => {
              setShouldSearch(true);
            }}
          >
            Search WOs
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <TablePagination
          component="div"
          count={recTotal}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sortDirection={orderBy === "work_orders.number" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "work_orders.number"}
                  direction={orderBy === "work_orders.number" ? order : "asc"}
                  onClick={() => handleSort("work_orders.number")}
                >
                  WO Number
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "items.number" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "items.number"}
                  direction={orderBy === "items.number" ? order : "asc"}
                  onClick={() => handleSort("items.number")}
                >
                  Item
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  orderBy === "work_orders.quantity" ? order : false
                }
              >
                <TableSortLabel
                  active={orderBy === "work_orders.quantity"}
                  direction={orderBy === "work_orders.quantity" ? order : "asc"}
                  onClick={() => handleSort("work_orders.quantity")}
                >
                  Quantity
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  orderBy === "work_orders.order_status" ? order : false
                }
              >
                <TableSortLabel
                  active={orderBy === "work_orders.order_status"}
                  direction={
                    orderBy === "work_orders.order_status" ? order : "asc"
                  }
                  onClick={() => handleSort("work_orders.order_status")}
                >
                  Order Status
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  orderBy === "work_orders.build_status" ? order : false
                }
              >
                <TableSortLabel
                  active={orderBy === "work_orders.build_status"}
                  direction={
                    orderBy === "work_orders.build_status" ? order : "asc"
                  }
                  onClick={() => handleSort("work_orders.build_status")}
                >
                  Build Status
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  orderBy === "work_orders.part_status" ? order : false
                }
              >
                <TableSortLabel
                  active={orderBy === "work_orders.part_status"}
                  direction={
                    orderBy === "work_orders.part_status" ? order : "asc"
                  }
                  onClick={() => handleSort("work_orders.part_status")}
                >
                  Part Status
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  orderBy === "work_orders.planned_start_date" ? order : false
                }
              >
                <TableSortLabel
                  active={orderBy === "work_orders.planned_start_date"}
                  direction={
                    orderBy === "work_orders.planned_start_date" ? order : "asc"
                  }
                  onClick={() => handleSort("work_orders.planned_start_date")}
                >
                  Start Date
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  orderBy === "work_orders.planned_completion_date"
                    ? order
                    : false
                }
              >
                <TableSortLabel
                  active={orderBy === "work_orders.planned_completion_date"}
                  direction={
                    orderBy === "work_orders.planned_completion_date"
                      ? order
                      : "asc"
                  }
                  onClick={() =>
                    handleSort("work_orders.planned_completion_date")
                  }
                >
                  Completion Date
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  orderBy === "work_orders.wo_date" ? order : false
                }
              >
                <TableSortLabel
                  active={orderBy === "work_orders.wo_date"}
                  direction={orderBy === "work_orders.wo_date" ? order : "asc"}
                  onClick={() => handleSort("work_orders.wo_date")}
                >
                  Created Date
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shouldSearch ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Box display="flex" gap={2}>
                    <CircularProgress size={24} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : foundWOs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    No Work Orders found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              foundWOs.map((wo) => (
                <TableRow key={wo.id} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, fontWeight: "medium" }}
                    >
                      {wo.number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, fontWeight: "medium" }}
                    >
                      {wo.item_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, fontWeight: "medium" }}
                    >
                      {`${parseFloat(wo.target_quantity)}x PCs`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={stringUtils.capFirstLetter(wo.order_status)}
                      color={statusUtils.getPrimaryOrderStatusColor(
                        wo.order_status,
                      )}
                      size="small"
                      clickable={false}
                      onClick={() => {}}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={stringUtils.capFirstLetter(wo.build_status)}
                      color={statusUtils.getWoBuildStatusColor(wo.build_status)}
                      size="small"
                      clickable={false}
                      onClick={() => {}}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={stringUtils.capFirstLetter(wo.part_status)}
                      color={statusUtils.getWoPartStatusColor(wo.part_status)}
                      size="small"
                      clickable={false}
                      onClick={() => {}}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="column" gap={2}>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        Planned:{" "}
                        {new Date(wo.planned_start_date).toLocaleDateString()}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        Actual:{" "}
                        {wo.actual_start_date
                          ? new Date(wo.actual_start_date).toLocaleDateString()
                          : "-"}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="column" gap={2}>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        Planned:{" "}
                        {new Date(
                          wo.planned_completion_date,
                        ).toLocaleDateString()}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        Actual:{" "}
                        {wo.actual_completion_date
                          ? new Date(
                              wo.actual_completion_date,
                            ).toLocaleDateString()
                          : "-"}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {new Date(wo.wo_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Stack direction="column" spacing={1}>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(
                                `/manufacturing/orders/form?mode=view&id=${wo.id}`,
                              )
                            }
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {![
                          "VOID",
                          "BUILD COMPLETE",
                          "PENDING APPROVAL",
                        ].includes(wo?.build_status) && (
                          <Tooltip title="Edit WO">
                            <IconButton
                              size="small"
                              onClick={() =>
                                navigate(
                                  `/manufacturing/orders/form?mode=edit&id=${wo.id}`,
                                )
                              }
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {![
                          "VOID",
                          "BUILD COMPLETE",
                          "PENDING APPROVAL",
                        ].includes(wo?.build_status) && (
                          <Tooltip title="Build WO">
                            <IconButton
                              size="small"
                              onClick={() =>
                                navigate(
                                  `/manufacturing/orders/form?mode=build&id=${wo.id}`,
                                )
                              }
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!["COMPLETE", "VOID"].includes(wo?.order_status) && (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip title="Complete WO">
                              <IconButton
                                size="small"
                                onClick={() => completeOrder(wo.id)}
                                color="success"
                                disabled={completeWOMutation.isPending}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="VOID WO">
                              <IconButton
                                size="small"
                                onClick={() => voidOrder(wo.id)}
                                color="error"
                                disabled={voidWOMutation.isPending}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                      {user?.is_superuser && (
                        <>
                          {!wo.is_record_active ? (
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Tooltip title="Reactivate WO">
                                <IconButton
                                  size="small"
                                  onClick={() => handleReactivate(wo.id)}
                                  color="success"
                                  disabled={reactivateWOMutation.isPending}
                                >
                                  <RestoreIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Tooltip title="Deactivate WO">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeactivate(wo.id)}
                                  color="error"
                                  disabled={deactivateWOMutation.isPending}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={recTotal}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>
    </Box>
  );
};
